'use client';

import { useTransition, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { toast } from 'sonner';
import { AlertTriangle, CalendarClock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PLANS } from '@/lib/plans';
import type { ExtendedUser } from '@/types/next-auth';
import type { LiveSubscription } from '@/lib/stripe/get-live-subscription';
import { createCheckoutSession } from '@/actions/billing/create-checkout-session';
import { createPortalSession } from '@/actions/billing/create-portal-session';
import { changeSubscriptionPlan } from '@/actions/billing/change-subscription-plan';

type PendingAction = null | 'portal' | 'STARTER' | 'PRO' | 'change-plan';

export function BillingSection({
  user,
  liveSubscription,
}: {
  user: ExtendedUser;
  liveSubscription: LiveSubscription | null;
}) {
  const [pending, setPending] = useState<PendingAction>(null);
  const [error, setError] = useState<string | null>(null);
  const [, startTransition] = useTransition();
  const router = useRouter();
  const { update: updateSession } = useSession();

  const plan = user.plan ?? 'FREE';
  const def = PLANS[plan];
  const isFree = plan === 'FREE';
  const isGrandfathered = Boolean(user.grandfathered);

  // All display fields come from the live Stripe subscription — always
  // accurate, never lags behind webhook events.
  const stripeStatus = liveSubscription?.status ?? null;
  const isCanceling = Boolean(liveSubscription?.isCanceling) && !isFree;
  const isPastDue = stripeStatus === 'past_due';
  const periodEndLabel = liveSubscription?.currentPeriodEnd
    ? new Date(liveSubscription.currentPeriodEnd).toLocaleDateString(
        undefined,
        {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }
      )
    : null;

  const goPortal = () => {
    setError(null);
    setPending('portal');
    startTransition(async () => {
      const res = await createPortalSession();
      if (res.url) {
        window.location.href = res.url;
      } else {
        setError(res.error ?? 'Failed to open billing portal.');
        setPending(null);
      }
    });
  };

  const upgradeTo = (target: 'STARTER' | 'PRO') => {
    setError(null);
    setPending(target);
    startTransition(async () => {
      const res = await createCheckoutSession({
        plan: target,
        interval: 'month',
      });
      if (res.url) {
        window.location.href = res.url;
      } else {
        setError(res.error ?? 'Failed to start checkout.');
        setPending(null);
      }
    });
  };

  const changePlan = (target: 'STARTER' | 'PRO') => {
    setError(null);
    setPending('change-plan');
    startTransition(async () => {
      const res = await changeSubscriptionPlan({ plan: target });
      if (res.success) {
        toast.success(`You're on ${PLANS[target].label} now.`);
        await updateSession?.();
        router.refresh();
      } else {
        setError(res.error ?? 'Failed to change plan.');
      }
      setPending(null);
    });
  };

  return (
    <div className="flex-1 max-w-xl">
      <h2 className="text-2xl font-bold mb-2">Billing</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Manage your subscription and billing details.
      </p>

      {isGrandfathered && (
        <Banner
          tone="info"
          icon={Sparkles}
          title="Legacy lifetime plan"
          body="All Pro features are unlocked for you at no cost — thank you for being an early supporter."
        />
      )}

      {isPastDue && (
        <Banner
          tone="danger"
          icon={AlertTriangle}
          title="Payment failed"
          body="We couldn't charge your card. Stripe will retry automatically, but please update your payment method to avoid losing access."
        />
      )}

      {isCanceling && !isPastDue && (
        <Banner
          tone="warning"
          icon={CalendarClock}
          title="Subscription canceled"
          body={`You'll keep ${def.label} access${
            periodEndLabel
              ? ` until ${periodEndLabel}`
              : ' until the end of your billing period'
          }, then your account will revert to Free. You can reactivate anytime from the billing portal before then.`}
        />
      )}

      <div className="rounded-xl border bg-background p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-muted-foreground">Current plan</p>
            <p className="text-2xl font-bold">{def.label}</p>
          </div>
          {def.priceMonthly > 0 && !isGrandfathered && (
            <p className="text-muted-foreground">€{def.priceMonthly}/mo</p>
          )}
        </div>
        {periodEndLabel && !isGrandfathered && (
          <p className="text-sm text-muted-foreground">
            {isCanceling ? 'Ends on' : 'Renews on'} {periodEndLabel}
          </p>
        )}
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg border border-destructive/40 bg-destructive/10 text-destructive text-sm">
          {error}
        </div>
      )}

      <BillingActions
        plan={plan}
        isGrandfathered={isGrandfathered}
        hasStripeCustomer={Boolean(user.stripeCustomerId)}
        isCanceling={isCanceling}
        isPastDue={isPastDue}
        pending={pending}
        onPortal={goPortal}
        onUpgrade={upgradeTo}
        onChangePlan={changePlan}
      />
    </div>
  );
}

/* --------------------------------- Banner --------------------------------- */

const BANNER_STYLES = {
  info: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200',
  warning:
    'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200',
  danger:
    'border-destructive/40 bg-destructive/10 text-destructive dark:border-destructive/40',
} as const;

function Banner({
  tone,
  icon: Icon,
  title,
  body,
}: {
  tone: keyof typeof BANNER_STYLES;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}) {
  return (
    <div
      className={`mb-6 rounded-lg border p-4 text-sm flex gap-3 ${BANNER_STYLES[tone]}`}
    >
      <Icon className="w-5 h-5 shrink-0 mt-0.5" />
      <div>
        <p className="font-semibold">{title}</p>
        <p className="mt-1 opacity-90">{body}</p>
      </div>
    </div>
  );
}

/* ------------------------------- Action area ------------------------------ */

function BillingActions({
  plan,
  isGrandfathered,
  hasStripeCustomer,
  isCanceling,
  isPastDue,
  pending,
  onPortal,
  onUpgrade,
  onChangePlan,
}: {
  plan: 'FREE' | 'STARTER' | 'PRO';
  isGrandfathered: boolean;
  hasStripeCustomer: boolean;
  isCanceling: boolean;
  isPastDue: boolean;
  pending: PendingAction;
  onPortal: () => void;
  onUpgrade: (target: 'STARTER' | 'PRO') => void;
  onChangePlan: (target: 'STARTER' | 'PRO') => void;
}) {
  // Grandfathered users have no billing actions — their plan is locked in.
  if (isGrandfathered) {
    return (
      <p className="text-sm text-muted-foreground">
        No billing actions available for legacy accounts.
      </p>
    );
  }

  // Paying user — send them to the Stripe portal to manage everything.
  if (plan !== 'FREE') {
    // Starter users on a healthy subscription also get an in-app upgrade
    // button so they don't have to dig into the portal for a simple
    // tier change (portal would still work if plan-switching is enabled
    // there, but this is one click and stays in-app).
    const canUpgradeInApp = plan === 'STARTER' && !isCanceling && !isPastDue;
    return (
      <div className="flex flex-col sm:flex-row gap-3">
        {canUpgradeInApp && (
          <Button
            onClick={() => onChangePlan('PRO')}
            disabled={pending !== null}
          >
            {pending === 'change-plan'
              ? 'Upgrading…'
              : `Upgrade to Pro — €${PLANS.PRO.priceMonthly}/mo`}
          </Button>
        )}
        <Button
          variant={canUpgradeInApp ? 'outline' : 'default'}
          onClick={onPortal}
          disabled={pending !== null}
        >
          {pending === 'portal'
            ? 'Loading…'
            : isCanceling
              ? 'Reactivate subscription'
              : 'Manage subscription'}
        </Button>
      </div>
    );
  }

  // Free user — show upgrade CTAs, plus a portal link if they previously had
  // a subscription (so they can view past invoices / update payment method).
  const starter = PLANS.STARTER;
  const pro = PLANS.PRO;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="outline"
          onClick={() => onUpgrade('STARTER')}
          disabled={pending !== null}
        >
          {pending === 'STARTER'
            ? 'Loading…'
            : `Upgrade to Starter — €${starter.priceMonthly}/mo`}
        </Button>
        <Button onClick={() => onUpgrade('PRO')} disabled={pending !== null}>
          {pending === 'PRO'
            ? 'Loading…'
            : `Upgrade to Pro — €${pro.priceMonthly}/mo`}
        </Button>
      </div>
      {hasStripeCustomer && (
        <button
          type="button"
          onClick={onPortal}
          disabled={pending !== null}
          className="text-sm text-muted-foreground underline-offset-4 hover:underline self-start disabled:opacity-50"
        >
          {pending === 'portal'
            ? 'Loading…'
            : 'View past invoices and payment methods'}
        </button>
      )}
    </div>
  );
}
